// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md#upstream-reachability
import * as React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import type { z } from 'zod';
import type {
  UpstreamReachabilityReportSchema,
  UpstreamReachabilityRequestSchema,
} from '@greenlight/contract';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import {
  CertificateTrustSection,
  type CertificateTrustSectionProps,
} from './CertificateTrustSection';
import { InsecureHttpWarning, type InsecureHttpWarningProps } from './IntegrationFields';

export type ReachabilityTargets = z.infer<typeof UpstreamReachabilityRequestSchema>;
export type ReachabilityReport = z.infer<typeof UpstreamReachabilityReportSchema>;
type Address = ReachabilityReport['addresses'][number];

/** Stable identity for a set of targets, so a result only counts for what it checked. */
export function reachabilityKey(targets: ReachabilityTargets | null): string | null {
  return targets ? JSON.stringify(targets) : null;
}

const ROLE_LABEL: Record<Address['role'], string> = {
  base_url: 'Base URL',
  token_url: 'Token URL',
  database: 'Database',
};

const STAGE_LABEL: Record<NonNullable<Address['stage']>, string> = {
  blocked: 'Refused by policy',
  dns: 'Name resolution failed',
  tcp: 'Connection failed',
  tls: 'TLS failed',
  http: 'No answer',
};

export interface RegisterConnectivityStepProps {
  /** Whether the form names addresses valid enough to check. */
  canCheck: boolean;
  checking: boolean;
  /** The result for the addresses as they stand; null until checked or after an edit. */
  report: ReachabilityReport | null;
  onCheck: () => void;
  /** A connected database's host and port fields, rendered above the check. */
  connection?: {
    host: string;
    port: string;
    onHostChange: (value: string) => void;
    onPortChange: (value: string) => void;
    hostError?: string;
    portError?: string;
  };
  /** Shown when the base URL is plain HTTP: the warning IT must acknowledge. */
  insecureHttp?: InsecureHttpWarningProps;
  /** Whether the base URL may be plain HTTP, which changes the prompt to enter one. */
  allowsPlainHttp?: boolean;
  /** The Certificate section, for an https:// base URL whose trust IT may set. */
  certificate?: Omit<CertificateTrustSectionProps, 'untrustedChain'>;
}

/**
 * The Register dialog's reachability check: the control plane dials each address
 * the integration's traffic will go to, with no credential. Register runs it too
 * when it has not passed for the addresses as they stand.
 */
export function RegisterConnectivityStep({
  canCheck,
  checking,
  report: current,
  onCheck,
  connection,
  insecureHttp,
  allowsPlainHttp = false,
  certificate,
}: RegisterConnectivityStepProps): React.JSX.Element {
  const untrustedChain =
    current?.addresses.some((a) => a.role === 'base_url' && a.untrusted_chain) ?? false;
  return (
    <fieldset className="space-y-3" data-testid="register-connectivity-step">
      <legend className="text-sm font-medium text-foreground">Connectivity</legend>

      {insecureHttp && <InsecureHttpWarning {...insecureHttp} />}

      {connection && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[2fr_1fr]">
          <div className="space-y-1.5">
            <Label htmlFor="reg-host">Host</Label>
            <Input
              id="reg-host"
              value={connection.host}
              onChange={(e) => connection.onHostChange(e.target.value)}
              placeholder="e.g. reporting.database.windows.net"
              className="font-mono"
            />
            {connection.hostError && (
              <p className="text-[11.5px] text-destructive">{connection.hostError}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reg-port">Port</Label>
            <Input
              id="reg-port"
              inputMode="numeric"
              value={connection.port}
              onChange={(e) => connection.onPortChange(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="1433"
              className="font-mono"
            />
            {connection.portError && (
              <p className="text-[11.5px] text-destructive">{connection.portError}</p>
            )}
          </div>
        </div>
      )}

      <p className="text-[11.5px] leading-relaxed text-muted-foreground">
        Greenlight checks that it can reach every address this integration uses before it saves. No
        credential is sent.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCheck}
          disabled={!canCheck || checking}
          data-testid="check-reachability"
        >
          {checking ? 'Checking…' : current ? 'Check again' : 'Check reachability'}
        </Button>
        {!canCheck && (
          <span className="text-[11.5px] text-muted-foreground">
            {connection
              ? 'Enter a host and port to check.'
              : allowsPlainHttp
                ? 'Enter an https or http URL to check.'
                : 'Enter an https URL to check.'}
          </span>
        )}
      </div>

      {current && (
        <div
          className="space-y-2 rounded-gl border border-border bg-muted/30 p-3"
          data-testid="reachability-result"
          role="status"
        >
          <ul className="space-y-1.5">
            {current.addresses.map((a) => (
              <li
                key={`${a.role}-${a.host}-${a.port}`}
                className="flex items-start gap-2 text-[12px]"
              >
                {a.reachable ? (
                  <CheckCircle2
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-green-text))]"
                    aria-hidden="true"
                  />
                ) : (
                  <XCircle
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-red-text))]"
                    aria-hidden="true"
                  />
                )}
                <span className="sr-only">{a.reachable ? 'Reachable' : 'Unreachable'}</span>
                <span className="space-y-0.5">
                  <span className="block">
                    <span className="text-muted-foreground">{ROLE_LABEL[a.role]} · </span>
                    <span className="font-mono">
                      {a.host}:{a.port}
                    </span>
                  </span>
                  {!a.reachable && (
                    <span className="block text-foreground">
                      {a.stage ? `${STAGE_LABEL[a.stage]}. ` : ''}
                      {a.message}
                    </span>
                  )}
                  {a.https_redirect && (
                    <span className="block text-foreground">
                      Redirects to <span className="font-mono">{a.https_redirect}</span>. Register
                      the HTTPS URL instead.
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[11.5px] leading-relaxed text-muted-foreground">
            {current.reachable ? 'Greenlight can reach every address.' : current.advice}
          </p>
        </div>
      )}

      {certificate && <CertificateTrustSection {...certificate} untrustedChain={untrustedChain} />}
    </fieldset>
  );
}
